/**
 * The Deployment fact of a run's Outcome, and the staff-only serve of a
 * grpo run whose own export failed. Such a run has a best step and no
 * deployment: staff on their own team may serve that step on a node of its
 * own (POST /api/training-runs/{run_id}/serve), which completes the run and
 * starts its held-out eval once the deployment is live. The api decides
 * what may be served; a refusal shows in the confirm dialog. On an
 * enterprise install the fact offers instead to record an SFT set run's
 * adapter served in the cluster (RecordDeploymentDialog, TRA-1600).
 */

import { useState } from 'react'

import { getApiErrorDetail } from '@/api/client'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { deploymentLabel, type TrainingPromotion } from '@/components/training/launch'
import { RecordDeploymentButton } from '@/components/training/RecordDeploymentDialog'
import { Button } from '@/components/ui/button'
import { useServeTrainingRun } from '@/hooks/useTrainingRuns'

interface DeploymentValueProps {
  runId: string
  promoted: TrainingPromotion | null
  /** The step a serve deploys (`servableStep`); null when the run has none to serve. */
  servable: string | null
  /** Whether staff may record the run's adapter served in the cluster (`recordableAdapter`). */
  recordable: boolean
  /** Staff on a team they belong to; the api 403s everyone else regardless. */
  canApprove: boolean
}

/** What the Deployment fact reads: the deployment, a button, or that there is none. */
export function DeploymentValue({
  runId,
  promoted,
  servable,
  recordable,
  canApprove,
}: DeploymentValueProps): React.JSX.Element {
  if (promoted !== null) {
    return <span className="font-mono text-xs">{deploymentLabel(promoted)}</span>
  }
  if (canApprove && servable !== null) return <ServeStepButton runId={runId} step={servable} />
  if (canApprove && recordable) return <RecordDeploymentButton runId={runId} />
  return <>Not deployed</>
}

/** Staff-only: serve the run's best step after a confirmation. */
export function ServeStepButton({ runId, step }: { runId: string; step: string }): React.JSX.Element {
  const serve = useServeTrainingRun()
  const [confirming, setConfirming] = useState(false)
  return (
    <>
      <Button
        variant="outline"
        onClick={() => {
          serve.reset()
          setConfirming(true)
        }}
      >
        Serve {step}
      </Button>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={`Serve ${step}`}
        description={
          <>
            A CPU node exports <span className="font-mono text-xs">{step}</span> and deploys it to
            Baseten on an H200. When the deployment is live, the run completes and its held-out eval
            starts. No training run can start until the serve ends.
          </>
        }
        confirmLabel="Serve step"
        pendingLabel="Serving..."
        isPending={serve.isPending}
        error={
          serve.isError ? (getApiErrorDetail(serve.error) ?? 'The serve did not start. Try again') : null
        }
        onConfirm={() => {
          serve.mutate(
            { runId },
            {
              onSuccess: () => {
                setConfirming(false)
              },
            },
          )
        }}
      />
    </>
  )
}
